import * as _ from "lodash";
import { IAmount } from "./amount";
import { ISigningInfo } from "./signing";
import { ICardCommissions } from "./topUp";

export type GeoControlMode =
    | "active"
    | "inactive"
    | "suspended"
    | "notAvailable";

export interface IGeoControlStatus {
    mode: GeoControlMode;
    suspendedUntil?: string;
}

export interface ICard {
    id: string;
    brand: string;
    creditCardShadowAccountId: string;
    owner: string;
    number: string;
    product: string;
    productI18N: string;
    productCode: string;
    expiryDate: string;
    limit: IAmount;
    flags: string[];
    features: string[];
    state: string;
    type: string;
    sequenceNumber: string;
    characteristic?: string;
    cardDeliveryMode: "HOME" | "BRANCH" | "BRANCH_PROLONGATION";
    signetCode: string;
    mainAccount: {
        iban: string;
        accountNo: {
            iban: string;
        };
        accountno: {
            iban: string;
        };
        holderName: string;
    };
    geoControlStatus: IGeoControlStatus;
    repaymentMethod: IRepaymentMethod;
    hasOpenPendingRequests: boolean;
    directDebitAccount?: {
        iban: string;
    };
    availableAmounts: {
        availableCredit: IAmount;
        actualCreditLimit: IAmount;
        actualBalance: IAmount;
        availableDailyPOSCashAdvance: IAmount;
        availableDailyPOSPurchase: IAmount;
        availableDailyATMCashAdvance: IAmount;
        availableDailyATMCashWithdrawal: IAmount;
        availableWeeklyATM: IAmount;
        availableWeeklyPOS: IAmount;
        dailyATMCashAdvanceLimit: IAmount;
        dailyATMCashWithdrawalLimit: IAmount;
        dailyPOSCashAdvance: IAmount;
        dailyPOSPurchase: IAmount;
        weeklyATMLimit: IAmount;
        weeklyPOSLimit: IAmount;
        weeklyTotalLimit: IAmount;
    };
    "at-prepaidCardManifestation": {
        cardVariant: "ERSTE01" | "ERSTE02";
        designCode: string;
        issueDate: string;
    };
}

export interface IRepaymentMethod {
    type: string; // "REVOLVING" | "CHARGE"
    percentage: number;
    minimumAmount: IAmount;
    payableMaximumAmount: IAmount;
}

export interface ICardResult {
    card: ICard;
}

export interface ICardLimit {
    limit: IAmount;
    shortCode: string;
}

export interface ICardLimits {
    limits: ICardLimit[];
}

export interface IPin {
    id: string;
    pin: {
        algorithm: string;
        encryptedPin: string;
    };
}

export type ISecuredInfoDisplayChannel = "SMS" | "OOB";
export interface ISecuredInfoDisplayChannelResponse {
    securedInfoDisplayChannel: ISecuredInfoDisplayChannel;
}

export interface ISecuredInfoResponsePost {
    signInfo: ISigningInfo;
}

export interface ITeamCardLinkageSMSOtpResponse {
    status: string;
    communicationId?: string;
}

export interface ISecuredInfoResponseGet {
    id: string;
    product: {
        type: string;
        id: string;
    };
    securedInfo: {
        securedInfoType: "CARD_PAN" | "CARD_CVC";
        encryptedData?: {
            algorithm: string;
            cipherText: string;
        };
        displayInterval?: number;
        "at-displayChannel"?: "SMS";
    }[];
}

export interface ICreditCardPurchaseRequest {
    productId: string;
    conditionsGroupId: string;
    creditCardLimit: string;
    payerAccountId: string;
    paybackMethod: string;
    paybackPercent: number;
    paybackAmount: IAmount;
    deliveryAddress?: string;
    infoAboSms?: string;
    infoAboEmail?: string;
    smsService: boolean;
    email: string;
    cardFormFactor?: string;
    milesAndMoreCardNumber?: string;
}

export interface IGetCreditCardUpgradeResponse {
    upgradeStatus: "OPEN" | "IN_PROGRESS" | "SUCCESS" | "FAILURE"; // OPEN=upgrade not started, IN_PROGRESS, SUCCESS, FAILURE
    card?: {
        // details of old card, only present if upgradeStatus=OPEN
        id: string;
        productCode: string; // key should be channelProduct
        product: string;
        limit: IAmount;
        fee: IAmount;
        feeFirstYear: IAmount;
    };
    offer?: {
        // new product details, only present for upgradeStatus=OPEN
        channelProduct: string; // product code of new card
        product: string;
        fee: IAmount;
        feeFirstYear: IAmount;
        supportsLoyaltyForex: boolean;
    };
    _links: {
        contractDocument: {
            href: string;
            title: string;
        };
        closingDocument: {
            href: string;
            title: string;
        };
        termsDocument: {
            href: string;
            title: string;
        };
        zadigDocument: {
            href: string;
            title: string;
        };
    };
}

export interface ICreditCardPurchaseResponse extends ICreditCardPurchaseRequest {
    signInfo: ISigningInfo;
}

export type PrepaidDesignCode =
    | "B0A"
    | "B0B"
    | "B08"
    | "B09" // Standard
    | "A05"
    | "A06"
    | "A07"
    | "A0C"; // Gift

export interface IPrepaidCardManifestation {
    cardVariant: "ERSTE01" | "ERSTE02" | "ERSTE03" | "ERSTE04";
    designCode?: PrepaidDesignCode;
    registrationDate?: string;
}

export interface IPrepaidCardPurchaseRequest {
    email: string;
    mobilePhoneNumber: string;
    address?: string;
    typeId: string;
    numberId: string;
    prepaidCardManifestation: IPrepaidCardManifestation;
    pin?: string;
    initialLoadAmount: IAmount;
    ibanPayer: string;
    acceptZadig: boolean;
}

export interface IPrepaidCardPurchasePutRequest {
    releaseKey: string;
    acceptTermsAndConditions: boolean;
    acceptPersonalDataProcessing: boolean;
    acceptCommercialCommunication: boolean;
}

export interface IPrepaidCardPurchaseResponse {
    orderId: string;
    email: string;
    mobilePhoneNumber: string;
    address: string;
    typeId: string;
    numberId: string;
    prepaidCardManifestation: IPrepaidCardManifestation;
    pin?: string;
    initialLoadAmount: IAmount;
    ibanPayer: string;
    acceptZadig: boolean;
    cardCommissions: ICardCommissions;
}

export interface IPrepaidCardPurchasePutResponse extends IPrepaidCardPurchaseResponse {
    releaseKey: string;
    acceptTermsAndConditions: boolean;
    acceptPersonalDataProcessing: boolean;
    acceptCommercialCommunication: boolean;
    signInfo: ISigningInfo;
}

export enum BankCardType {
    UNKNOWN,
    MICRO,
    STICKER,
    MOBILE,
}

export interface IBankCardMicroPurchaseRequest {
    bankCardId: string;
    emailContactId: string;
}

export interface IBankCardMicroPurchaseResponse extends IBankCardMicroPurchaseRequest {
    signInfo: ISigningInfo;
}

export interface IBankCardMobilePurchaseRequest extends IBankCardMicroPurchaseRequest {
    mobilePhoneContactId: string;
    activationCode: string;
}

export interface IBankCardMobilePurchaseResponse extends IBankCardMobilePurchaseRequest {
    signInfo: ISigningInfo;
}

export interface IRevolvingResponse {
    creditCardRevolvingType: {
        minAmount?: IAmount;
        percentage: number;
        phoneId: string;
        sendSmsNotification: boolean;
    };
    signInfo: ISigningInfo;
}

export interface ICreditCardRevolving {
    percentage: number;
    minAmount: IAmount;

    // SMS notification is optional
    sendSmsNotification?: boolean;
    phoneId?: string;
}

export interface IBase64File {
    contentBase64: string; // encoded data (decode with atob())
    contentType: string; // should be "application/pdf"
    name: string; // file name
}

export interface IGeoControlSuspendResponse {
    geoControlSuspensionType: {
        cardId: string;
        suspendedUntil: Date;
    };
    signInfo: ISigningInfo;
}

export interface IGeoControlActivateResponse {
    geoControlActivationType: { cardId: string };
    signInfo: ISigningInfo;
}

export interface ICardStateResponse {
    stateInformationType: {
        action: string;
        cardId?: string;
        closeReason?: string;
        payeeAccount?: string;
    };
    balanceCommission?: IAmount;
    collectCommissions?: IAmount;
    signInfo: ISigningInfo;
}

export interface IPrepaidCodeMatrix {
    matrixHeader: string;
    matrixFooter: string;
}

export type ICharacteristicEnum =
    | "MAIN"
    | "BUSINESS"
    | "AUTHORIZED"
    | "AUTHORIZED_WITH_STANDALONE_DEBIT_ACCOUNT";

export type ILoyaltyGeorgeBusiness =
    | "TRAVELINSURANCE"
    | "FASTTRACK"
    | "SMARTDELAY"
    | "AUTO_FOREX"
    | "CAR_RENTAL_SIXT"
    | "CAR_RENTAL_HERTZ"
    | "CAR_RENTAL_HERTZ_MILES_AND_MORE"
    | "CAR_RENTAL_AVIS"
    | "CAR_RENTAL_AVIS_MILES_AND_MORE"
    | "CAR_RENTAL_RENTALCARS_MILES_AND_MORE"
    | "VISALUXURYHOTELS"
    | "TAXBACK"
    | "EPAPER";

export type ILoyaltyDataType =
    | "MILES_AND_MORE"
    | "MILES_AND_MORE_PAY_CARD_FEE_IN_MILES"
    | "CASHBACK"
    | "FOREX"
    | "INSTALLMENT"
    | "AIRPORTSERVICE"
    | "HOLIDAYBONUS"
    | "PRIORITYPASS"
    | "ATM"
    | "AIR_REFUND"
    | "BUSINESS_CASHBACK"
    | ILoyaltyGeorgeBusiness;

type ILoyaltyDetails = {
    maxPossibleLoyaltyActions?: number;
    maxLoyaltyAmount?: IAmount;
    usedLoyaltyActions?: number;
    totalAmountRefunded?: IAmount;
    totalAmountFeesSaved?: IAmount;
};

export type ILoyaltyDataLinksType = {
    historyCurrentPeriod?: {
        href: string;
    };
    historyLastPeriod?: {
        href: string;
    };
    historyMorePeriods?: {
        href: string;
    };
    historyLastYear?: {
        href: string;
    };
    newRefunds?: {
        href: string;
    };
    participatingMerchants?: {
        href: string;
    };
    airportServices: {
        href: string;
    };
    holidayBonus: {
        href: string;
    };
    priorityPassLandingPage: {
        href: string;
    };
    priorityPassAppHelpPage: {
        href: string;
    };
    travelInsurance: {
        href: string;
    };
    smartDelay: {
        href: string;
    };
    visaLuxuryHotels: {
        href: string;
    };
    taxBack: {
        href: string;
    };
    ePaperLandingPage: {
        href: string;
    };
    carRentalHertz: {
        href: string;
    };
    carRentalSixtLandingPage: {
        href: string;
    };
    carRentalSixtRegistrationPage: {
        href: string;
    };
    carRentalAvis: {
        href: string;
    };
    carRentalLandingPage: {
        href: string;
    };
    carRentalCars: {
        href: string;
    };
    airRefund: {
        href: string;
    };
    milesandmore: {
        href: string;
    };
    austrian: {
        href: string;
    };
    mmPayCardsFeeInMilesInformation?: {
        href: string;
    };
};

export type ILoyaltyData = ILoyaltyDetails & {
    "at-loyaltyPercentage"?: number;
    "at-loyaltyPercentage2"?: number;
    loyaltyBenefitCode?: string;
    loyaltyBenefitName?: string;
    loyaltyBenefitAddon?: string;
    loyaltyBenefitThreshold?: number;
    type: ILoyaltyDataType;
    maxPossibleLoyaltyActions?: number;
    maxLoyaltyAmount?: IAmount;
    productName?: string;
    productShortCode?: string;
    usedLoyaltyActions?: number;
    totalAmountRefunded?: IAmount;
    totalAmountFeesSaved?: IAmount;
    periodLoyaltyBonusCollected?: number;
    totalLoyaltyBonusCollected?: number;
    loyaltyPeriodStart?: string;
    loyaltyPeriodEnd?: string;
    loyaltyProgramMembershipStatus?: string;
    loyaltyProgramMembershipNumber?: string;
    payCardsFeeInMilesActivated?: boolean;
    payCardsFeeInMilesChangeable?: boolean;
    summary?: ILoyaltyDetails &
        {
            cardId: string;
            cardHolderName: string;
            maskedPAN: string;
        }[];
    _links: ILoyaltyDataLinksType;
};

export type ILoyaltyDataResponse = {
    cardHolderName: string;
    cardId: string;
    characteristic: ICharacteristicEnum;
    loyaltyData: ILoyaltyData[];
    productName: string;
    productShortCode: string;
};

/** GET /cards/{id}/loyalty/MILES_AND_MORE_PAY_CARD_FEE_IN_MILES/ */
export type IPayCardFeeInMilesDetailResponse = {
    payCardsFeeInMilesActivated?: boolean;
    _links?: {
        mmPayCardsFeeInMilesInformation?: { href: string };
    };
};

/** PUT /cards/{id}/loyalty/MILES_AND_MORE_PAY_CARD_FEE_IN_MILES/ */
export type IPayCardFeeInMilesPutResponse = {
    signInfo: ISigningInfo;
};

export type ILoyaltyHistoryTransaction = {
    transactionId: string;
    cardId: string;
    maskedPAN: string;
    cardHolderName: string;
    originalTrxAmount: IAmount;
    atmFee?: {
        atmfee: IAmount;
        exchangeRateValue?: number;
        exchangeRatePrecision?: number;
    };
    forexFee?: {
        forexfee: IAmount;
        exchangeRateValue?: number;
        exchangeRatePrecision?: number;
    };
    transactionDate: Date;
    bookingDate: Date;
    transactionName: string;
    merchantName: string;
    transactionType?: string;
};

export type ILoyaltyHistoryResponse = {
    loyaltyTpe: ILoyaltyDataType;
    trxCount: number;
    totalSumSaved: IAmount;
    transactions: ILoyaltyHistoryTransaction[];
    periodStart?: string;
    periodEnd?: string;
    periodMiles?: number;
    // Miles & More specific fields
    loyaltyPeriodStart?: string;
    loyaltyPeriodEnd?: string;
    periodLoyaltyBonusCollected?: number;
    totalLoyaltyBonusCollected?: number;
    loyaltyProgramMembershipNumber?: string;
    loyaltyProgramMembershipStatus?: string;
};

export type IBillingPeriod = {
    periodStart: string;
    periodEnd: string;
    totalMiles: number;
    transactions: ILoyaltyHistoryTransaction[];
    periodIndex: number;
    isCurrent: boolean;
    isPrevious: boolean;
};

export type ICardNameValidResponse = {
    nameCardline1: string;
    nameCardline2: string;
    isValid: boolean;
};

export type ILoyaltyForexTransaction = {
    transactionId: string;
    amount: IAmount;
    creationDate?: Date;
    paymentAmount?: IAmount;
    cardId: string;
    maskedPAN?: string;
    cardHolderName?: string;
    atmFee?: IAmount;
    forexFee?: IAmount;
    bookingDate?: Date;
    bookingText?: string;
    merchantName?: string;
};

export type ILoyaltyForexTransactionProductType = "ACCOUNTS" | "CARDS";

export type ILoyaltyForexTransactionsResponse = {
    transactions: {
        product: {
            type: ILoyaltyForexTransactionProductType;
            id: string;
        };
        transactions: ILoyaltyForexTransaction[];
    }[];
    pageNumber: number;
    pageCount: number;
    nextPage: number;
    pageSize: number;
};

export type ILoyaltyForexRefundRequest = {
    requestRefunds: {
        product: {
            type: ILoyaltyForexTransactionProductType;
            id: string;
        };
        transactionIds: string[];
    }[];
};

export type ILoyaltyForexRefundResponse = {
    status?: {
        product: {
            type: ILoyaltyForexTransactionProductType;
            id: string;
        };
        transactions: {
            transactionId: string;
            status: "REQUESTED";
        }[];
    }[];
    signInfo: ISigningInfo;
};

export type ICardTanCard = {
    physicalCardNumber: string;
    account: string;
    accountDescription: string;
    accountInstitute: number;
    sequenceNumber: number;
    status: "ACTIVE" | string; // TODO type properly
    validUntil: string;
    flags?: string[];
};

export type IGetCardTanCardsResponse = {
    cardTanCards: ICardTanCard[];
    pageCount: number;
    pageNumber: number;
    pageSize: number;
};

export type IPutCardTanCardsResponse = {
    cardTanCards: ICardTanCard[];
    signInfo: ISigningInfo;
};

export type IPostOrderNewCardTanReaderResponse = {
    deliveryAddressId: string;
    costsPerReader: IAmount;
    totalCostsOfReaders: IAmount;
    signInfo: ISigningInfo;
    numberOfReaders: string;
    emailContactId: string;
};

export function supportsRevolving(card: ICard) {
    return card.features && _.includes(card.features, "revolving");
}

export function supportsDisplayPin(card: ICard) {
    return card.features && card.features.indexOf("displayPin") >= 0;
}

export function supportsQuickDrawDown(card: ICard) {
    return card.features && card.features.indexOf("quickDrawDown") >= 0;
}

export function supportsGeoControl(card: ICard) {
    return card.features && card.features.indexOf("geoControl") >= 0;
}

export function supportsReissueCard(card: ICard) {
    return (
        card.features &&
        card.features.indexOf("reissueCard") >= 0 &&
        !isPrepaid(card)
    );
}

export function supportsReissueCardAndUpgrade(card: ICard) {
    return (
        card.features &&
        card.features.indexOf("reissueCard") >= 0 &&
        !isPrepaid(card) &&
        supportsUpgrade(card)
    );
}

export function supportsActivateCard(card: ICard) {
    return card.features && card.features.indexOf("activateCard") >= 0;
}

export function isLocked(card: ICard) {
    return card.flags && card.flags.indexOf("locked") >= 0;
}

export function isClosed(card: ICard) {
    return card.flags && card.flags.indexOf("closed") >= 0;
}

export function isCreditCard(card: ICard) {
    return card.type === "CREDIT";
}

export function isPrepaid(card: ICard) {
    return card.type === "PREPAID";
}

export function isPrepaidOnline(card: ICard) {
    return (
        card.type === "PREPAID" &&
        card["at-prepaidCardManifestation"].cardVariant === "ERSTE02"
    );
}

export function isBankCard(card: ICard) {
    return card.type === "BANK_CARD";
}

export function isMicroChip(card: ICard) {
    return isBankCard(card) && card.flags.indexOf("microChip") >= 0;
}

export function isMicroSticker(card: ICard) {
    return isBankCard(card) && card.flags.indexOf("microSticker") >= 0;
}

export function supportsUpgrade(card: ICard) {
    return (
        card.features &&
        _.includes(card.features, "at-creditCardMigrationExternal")
    );
}



// WEBPACK FOOTER //
// ./src/types/card.ts